// Editor.js block tool for a "bar list": labels each followed by a long
// accent-coloured bar, part-filled to a percentage (the highlighted
// focus-areas / skill-bar pattern). Stores
//   { items: [{ label, percent }], align: "left" | "right", color? }
// (legacy items were plain strings = a full bar). Rendered by the template's
// render-blocks.ts; the bar colour defaults to the theme accent, overridable
// via the shared colour row.
//
// config: { getPalette } — tenant palette colours for the colour row.

import { colorRow } from "./cta-tool.js";

const ICON =
  '<svg width="17" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="3" y1="6" x2="10" y2="6"/><line x1="13" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="8" y2="12"/><line x1="11" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="12" y2="18"/><line x1="15" y1="18" x2="21" y2="18"/></svg>';

const ALIGNMENTS = [
  { value: "left", label: "Labels left" },
  { value: "right", label: "Labels right" },
];

const MAX_ITEMS = 20;

function clampPercent(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n)) return 100;
  return Math.min(Math.max(Math.round(n), 0), 100);
}

/** One textarea line → { label, percent }: an optional trailing "| 60" (or
 *  "| 60%") sets the fill; anything else is a full bar. */
function parseLine(line) {
  const pipe = line.lastIndexOf("|");
  if (pipe !== -1) {
    const tail = line.slice(pipe + 1).trim().replace(/%$/, "");
    if (/^\d{1,3}$/.test(tail)) {
      return { label: line.slice(0, pipe).trim(), percent: clampPercent(Number(tail)) };
    }
  }
  return { label: line.trim(), percent: 100 };
}

function itemLine(item) {
  return item.percent === 100 ? item.label : `${item.label} | ${item.percent}`;
}

export class BarListTool {
  static get toolbox() {
    return { title: "Bar list", icon: ICON };
  }

  constructor({ data, config, block }) {
    this.block = block;
    this.config = config ?? {};
    this.data = {
      items: (Array.isArray(data?.items) ? data.items : [])
        .map((item) =>
          typeof item === "string"
            ? { label: item, percent: 100 }
            : { label: typeof item?.label === "string" ? item.label : "", percent: clampPercent(item?.percent) })
        .filter((item) => item.label)
        .slice(0, MAX_ITEMS),
      align: data?.align === "right" ? "right" : "left",
      color: typeof data?.color === "string" ? data.color : "",
    };
  }

  render() {
    const root = document.createElement("div");
    root.className = "pgs-testimonials-tool";

    const bar = document.createElement("div");
    bar.className = "pgs-gallery-tool__bar";
    const title = document.createElement("span");
    title.className = "pgs-gallery-tool__title";
    title.textContent = "Bar list";

    const toggle = document.createElement("div");
    toggle.className = "pgs-gallery-tool__toggle";
    this._alignButtons = ALIGNMENTS.map(({ value, label }) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "pgs-gallery-tool__btn";
      button.textContent = label;
      button.addEventListener("click", () => {
        this.data.align = value;
        this._applyAlign();
        this.block?.dispatchChange?.();
      });
      toggle.appendChild(button);
      return { value, button };
    });
    this._applyAlign();

    bar.append(title, toggle);

    const body = document.createElement("div");
    body.className = "pgs-bar-list-tool__body";

    const textarea = document.createElement("textarea");
    textarea.className = "pgs-testimonials-tool__quote";
    textarea.rows = Math.min(Math.max(this.data.items.length + 1, 4), 12);
    textarea.placeholder =
      'One label per line — add "| 60" to part-fill the bar, e.g.\nCUSTOM SITES | 90\nBRANDING DESIGN | 75\nSEO';
    textarea.value = this.data.items.map(itemLine).join("\n");
    textarea.addEventListener("input", () => {
      this.data.items = textarea.value
        .split("\n")
        .map(parseLine)
        .filter((item) => item.label)
        .slice(0, MAX_ITEMS);
      this.block?.dispatchChange?.();
    });

    const hint = document.createElement("p");
    hint.className = "pgs-bar-list-tool__hint";
    hint.textContent = "Bars fill to the given percentage (no number = full bar).";

    const colorLabel = document.createElement("p");
    colorLabel.className = "pgs-bar-list-tool__hint";
    colorLabel.textContent = "Bar colour";

    body.append(
      textarea,
      hint,
      colorLabel,
      colorRow(this.data.color, this.config.getPalette?.() ?? [], (color) => {
        this.data.color = color;
        this.block?.dispatchChange?.();
      })
    );

    root.append(bar, body);
    return root;
  }

  _applyAlign() {
    for (const { value, button } of this._alignButtons) {
      button.classList.toggle("pgs-gallery-tool__btn--active", this.data.align === value);
    }
  }

  save() {
    return {
      items: this.data.items,
      align: this.data.align,
      ...(this.data.color ? { color: this.data.color } : {}),
    };
  }
}
